import type { DatabaseClient } from '../storage-backend.js'
import { z } from 'zod'
import { AttachmentsRepository } from '../repositories/attachments-repository.js'
import type { AttachmentRow } from '../repositories/attachments-repository.js'
import type { BlobStore } from '../blob-store.js'
import { enqueueJob } from '../job-queue-worker.js'

export const ManageAttachmentsInputSchema = z.object({
  action: z.enum(['attach', 'list', 'get', 'get_blob', 'delete']),
  note_id: z.string().optional(),
  attachment_id: z.string().optional(),
  /** Base64-encoded file data for the 'attach' action */
  data_base64: z.string().optional(),
  filename: z.string().optional(),
  mime_type: z.string().optional(),
  extracted_text: z.string().optional(),
  display_name: z.string().optional(),
})
export type ManageAttachmentsInput = z.infer<typeof ManageAttachmentsInputSchema>

/**
 * Project a repository row to the tool's pinned JSON surface.
 *
 * The tool contract is an explicit allowlist — schema migrations (0017 adds
 * status/extraction/preview columns) MUST NOT silently widen the tool JSON.
 * Widening it is a deliberate format-parity decision made against the server
 * tool surface, not a side effect of `SELECT *`.
 */
function toToolAttachment(row: AttachmentRow): AttachmentRow {
  return {
    id: row.id,
    note_id: row.note_id,
    blob_id: row.blob_id,
    document_type_id: row.document_type_id,
    mime_type: row.mime_type,
    extracted_text: row.extracted_text,
    filename: row.filename,
    display_name: row.display_name,
    position: row.position,
    created_at: row.created_at,
    deleted_at: row.deleted_at,
  }
}

export interface ManageAttachmentsResult {
  action: string
  attachment?: AttachmentRow
  attachments?: AttachmentRow[]
  attachment_id?: string
  /** Base64-encoded blob data for 'get_blob' action */
  data_base64?: string
  size_bytes?: number
}

export async function manageAttachments(
  db: DatabaseClient,
  blobStore: BlobStore,
  rawInput: unknown,
): Promise<ManageAttachmentsResult> {
  const input = ManageAttachmentsInputSchema.parse(rawInput)
  const repo = new AttachmentsRepository(db, blobStore)

  switch (input.action) {
    case 'attach': {
      if (!input.note_id) throw new Error('note_id required for attach')
      if (!input.data_base64) throw new Error('data_base64 required for attach')
      if (!input.filename) throw new Error('filename required for attach')

      // Decode base64 to Uint8Array
      const binaryStr = atob(input.data_base64)
      const data = new Uint8Array(binaryStr.length)
      for (let i = 0; i < binaryStr.length; i++) data[i] = binaryStr.charCodeAt(i)

      const attachment = await repo.attach({
        noteId: input.note_id,
        data,
        filename: input.filename,
        mimeType: input.mime_type,
        extractedText: input.extracted_text,
        displayName: input.display_name,
      })
      if (input.extracted_text) {
        await enqueueJob(db, { noteId: input.note_id, jobType: 'embedding' })
        await enqueueJob(db, { noteId: input.note_id, jobType: 'concept_tagging' })
      }
      return { action: 'attach', attachment: toToolAttachment(attachment), size_bytes: data.length }
    }
    case 'list': {
      if (!input.note_id) throw new Error('note_id required for list')
      const attachments = await repo.list(input.note_id)
      return { action: 'list', attachments: attachments.map(toToolAttachment) }
    }
    case 'get': {
      if (!input.attachment_id) throw new Error('attachment_id required for get')
      const attachment = await repo.get(input.attachment_id)
      return { action: 'get', attachment: toToolAttachment(attachment) }
    }
    case 'get_blob': {
      if (!input.attachment_id) throw new Error('attachment_id required for get_blob')
      const blob = await repo.getBlob(input.attachment_id)
      if (!blob) throw new Error(`Blob not found for attachment ${input.attachment_id}`)

      // Encode Uint8Array to base64 for JSON transport
      let binary = ''
      for (let i = 0; i < blob.length; i++) binary += String.fromCharCode(blob[i])
      const data_base64 = btoa(binary)

      return { action: 'get_blob', attachment_id: input.attachment_id, data_base64, size_bytes: blob.length }
    }
    case 'delete': {
      if (!input.attachment_id) throw new Error('attachment_id required for delete')
      const attachment = await repo.get(input.attachment_id)
      await repo.delete(input.attachment_id)
      if (attachment.extracted_text) {
        await enqueueJob(db, { noteId: attachment.note_id, jobType: 'embedding' })
        await enqueueJob(db, { noteId: attachment.note_id, jobType: 'concept_tagging' })
      }
      return { action: 'delete', attachment_id: input.attachment_id }
    }
  }
}
